import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { Code, Copy, Check, Eye, Settings, Type, Palette, Loader2, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface EmbedStyleConfig {
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  borderRadius: string;
  padding: string;
  width: string;
}

interface OpeningHoursEmbedEditorProps {
  restaurantId?: string | number;
  menuId?: string | number;
}

const OpeningHoursEmbedEditor: React.FC<OpeningHoursEmbedEditorProps> = ({ restaurantId, menuId }) => {
  const { t } = useTranslation();
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [specialHours, setSpecialHours] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [isStyleConfigOpen, setIsStyleConfigOpen] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  
  const [styleConfig, setStyleConfig] = useState<EmbedStyleConfig>({
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: '400',
    textColor: '#111827',
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
    borderRadius: '8px',
    padding: '16px',
    width: '100%'
  });

  // Check for mobile view on mount and resize
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  useEffect(() => {
    generateEmbedCode();
  }, [restaurantId, menuId, styleConfig, selectedDate]);
  
  // Fetch preview data when restaurantId changes
  useEffect(() => {
    if (restaurantId) {
      fetchPreviewData();
      fetchSpecialHours();
    }
  }, [restaurantId]);

  const generateEmbedCode = async () => {
    setLoading(true);
    try {
      const id = restaurantId || menuId;
      // Extract the API base URL without the /api suffix
      const apiBaseURL = axios.defaults.baseURL || 'http://localhost:5000/api';
      const baseUrl = apiBaseURL.replace(/\/api$/, ''); // Remove trailing /api if present
      
      // Use selected date if available, otherwise use today
      const checkDate = selectedDate ? new Date(selectedDate) : new Date();
      const dateString = checkDate.toISOString().split('T')[0];
      
      // Generate embed code with style configuration
      const styleString = `
        font-family: ${styleConfig.fontFamily}, sans-serif;
        font-size: ${styleConfig.fontSize};
        font-weight: ${styleConfig.fontWeight};
        color: ${styleConfig.textColor};
        background-color: ${styleConfig.backgroundColor};
        border: 1px solid ${styleConfig.borderColor};
        border-radius: ${styleConfig.borderRadius};
        padding: ${styleConfig.padding};
        width: ${styleConfig.width};
      `.trim().replace(/\s+/g, ' ');

      const code = `<div id="menu-digitale-hours-${id}" style="${styleString}"></div>
<script>
  (function() {
    const container = document.getElementById('menu-digitale-hours-${id}');
    if (!container) return;
    
    const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    // Use the selected date from the embed editor
    const checkDate = new Date('${dateString}');
    const dateString = '${dateString}';
    
    fetch('${baseUrl}/api/restaurants/${id}/public-opening-hours?date=' + dateString)
      .then(res => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json();
      })
      .then(data => {
        if (data.success && data.data) {
          const hours = data.data;
          // Normalize slot: API may return JSON string or object
          function parseSlot(slot) {
            if (!slot) return null;
            if (typeof slot === 'string') {
              try { var o = JSON.parse(slot); return o && (o.open !== undefined || o.close !== undefined) ? o : null; } catch (e) { return null; }
            }
            return slot && (slot.open !== undefined || slot.close !== undefined) ? slot : null;
          }
          let html = '<div style="font-family: ${styleConfig.fontFamily}, sans-serif;">';
          
          // Check if we're showing special hours
          let isShowingSpecialHours = false;
          const dayOfWeek = dayKeys[checkDate.getDay()];
          
          // Add title with special hours indicator
          html += '<div style="font-weight: bold; margin-bottom: 12px; font-size: 1.1em;">Orari Apertura';
          
          // Check if the selected date has special hours
          if (hours[dayOfWeek] && hours[dayOfWeek].isSpecial) {
            const displayDate = checkDate.toLocaleDateString('it-IT', { 
              weekday: 'long', 
              month: 'short', 
              day: 'numeric' 
            });
            html += ' <span style="color: #3B82F6; font-size: 0.9em;">(Orari speciali applicati ' + displayDate + ')</span>';
            isShowingSpecialHours = true;
          }
          
          html += '</div>';
          
          // Group consecutive days with same hours
          const groups = [];
          let currentGroup = null;
          
          // Process days in order (Mon-Sun)
          const orderedDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          
          for (let i = 0; i < orderedDays.length; i++) {
            const dayKey = orderedDays[i];
            const dayHours = hours[dayKey];
            const lunch = dayHours ? parseSlot(dayHours.lunchSlot) : null;
            const dinner = dayHours ? parseSlot(dayHours.dinnerSlot) : null;
            const single = dayHours ? parseSlot(dayHours.singleSlot) : null;
            
            // Create a signature for comparison. Prefer split when both lunch+dinner have open/close.
            let signature = '';
            if (dayHours && dayHours.isOpen) {
              if (lunch && dinner && lunch.open && lunch.close && dinner.open && dinner.close) {
                signature = 'split:' + lunch.open + '-' + lunch.close + ':' + dinner.open + '-' + dinner.close;
              } else if (single && single.open && single.close) {
                signature = 'single:' + single.open + '-' + single.close;
              } else {
                signature = 'closed';
              }
            } else {
              signature = 'closed';
            }
            
            if (!currentGroup || currentGroup.signature !== signature) {
              if (currentGroup) {
                groups.push(currentGroup);
              }
              currentGroup = {
                days: [dayKey],
                signature: signature,
                hours: dayHours
              };
            } else {
              currentGroup.days.push(dayKey);
            }
          }
          
          // Add the last group
          if (currentGroup) {
            groups.push(currentGroup);
          }
          
          // Render grouped days
          html += '<div style="display: flex; flex-direction: column; gap: 8px;">';
          
          groups.forEach(function(group) {
            var dayLabels = group.days.map(function(day) {
              // Map English day keys to Italian abbreviations
              var italianDays = {
                'monday': 'Lun',
                'tuesday': 'Mar',
                'wednesday': 'Mer',
                'thursday': 'Gio',
                'friday': 'Ven',
                'saturday': 'Sab',
                'sunday': 'Dom'
              };
              return italianDays[day] || day.charAt(0).toUpperCase() + day.slice(1).substring(0, 3);
            });
            
            var dayDisplay = '';
            if (group.days.length === 1) {
              dayDisplay = dayLabels[0];
            } else if (group.days.length === 2) {
              dayDisplay = dayLabels[0] + ' & ' + dayLabels[1];
            } else {
              dayDisplay = dayLabels[0] + ' - ' + dayLabels[dayLabels.length - 1];
            }
            
            html += '<div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #E5E7EB;">';
            html += '<span style="font-weight: 500; min-width: 120px;">' + dayDisplay + ':</span>';
            
            if (group.hours && group.hours.isOpen) {
              if (group.signature.indexOf('single:') === 0) {
                var timeParts = group.signature.substring(7).split('-');
                var open = timeParts[0];
                var close = timeParts[1];
                html += '<span>' + formatTime24(open) + ' ' + translateTerm('to') + ' ' + formatTime24(close) + '</span>';
              } else if (group.signature.indexOf('split:') === 0) {
                var timePart = group.signature.substring(6);
                var parts = timePart.split(':');
                var lunchOpen = parts[0] + ':' + (parts[1] && parts[1].split('-')[0] ? parts[1].split('-')[0] : '00');
                var lunchClose = (parts[1] && parts[1].split('-')[1] ? parts[1].split('-')[1] : '00') + ':' + (parts[2] || '00');
                var dinnerOpen = (parts[3] || '00') + ':' + (parts[4] && parts[4].split('-')[0] ? parts[4].split('-')[0] : '00');
                var dinnerClose = (parts[4] && parts[4].split('-')[1] ? parts[4].split('-')[1] : '00') + ':' + (parts[5] || '00');
                html += '<span style="display: flex; flex-direction: column; text-align: right;">';
                html += '<span>' + translateTerm('Lunch:') + ' ' + formatTime24(lunchOpen) + ' - ' + formatTime24(lunchClose) + '</span>';
                html += '<span style="margin-top: 4px;">' + translateTerm('Dinner:') + ' ' + formatTime24(dinnerOpen) + ' - ' + formatTime24(dinnerClose) + '</span>';
                html += '</span>';
              } else {
                // Fallback: parse and render from group.hours (handles API returning slots as JSON strings)
                var h = group.hours;
                var fLunch = h ? parseSlot(h.lunchSlot) : null;
                var fDinner = h ? parseSlot(h.dinnerSlot) : null;
                var fSingle = h ? parseSlot(h.singleSlot) : null;
                if (fLunch && fDinner && fLunch.open && fLunch.close && fDinner.open && fDinner.close) {
                  html += '<span style="display: flex; flex-direction: column; text-align: right;">';
                  html += '<span>' + translateTerm('Lunch:') + ' ' + formatTime24(fLunch.open) + ' - ' + formatTime24(fLunch.close) + '</span>';
                  html += '<span style="margin-top: 4px;">' + translateTerm('Dinner:') + ' ' + formatTime24(fDinner.open) + ' - ' + formatTime24(fDinner.close) + '</span>';
                  html += '</span>';
                } else if (fSingle && fSingle.open && fSingle.close) {
                  html += '<span>' + formatTime24(fSingle.open) + ' ' + translateTerm('to') + ' ' + formatTime24(fSingle.close) + '</span>';
                } else {
                  html += '<span style="color: #9CA3AF;">Orari non disponibili</span>';
                }
              }
            } else {
              html += '<span style="color: #EF4444; font-weight: 500;">Chiuso</span>';
            }
            
            // Show special hours indicator for the selected date
            if (group.hours && group.hours.isSpecial) {
              html += '<br><span style="color: #3B82F6; font-size: 0.8em; font-style: italic;">Orari speciali applicati</span>';
            }
            
            html += '</div>';
          });
          
          html += '</div></div>';
          
          // Format time as 24h HH:MM (pad hour and minute to 2 digits)
          function formatTime24(timeStr) {
            if (!timeStr) return '';
            var p = (timeStr + '').trim().split(':');
            var h = parseInt(p[0], 10);
            var m = p[1] != null ? parseInt(p[1], 10) : 0;
            if (isNaN(h)) h = 0;
            if (isNaN(m)) m = 0;
            return (h < 10 ? '0' + h : '' + h) + ':' + (m < 10 ? '0' + m : '' + m);
          }
          
          // Helper function to translate time-related terms
          function translateTerm(term) {
            var translations = {
              'Lunch:': 'Pranzo:',
              'Dinner:': 'Cena:',
              'to': 'a'
            };
            return translations[term] || term;
          }
          
          container.innerHTML = html;
        } else {
          container.innerHTML = '<div style="color: #EF4444;">Orari non disponibili</div>';
        }
      })
      .catch(function(err) {
        console.error('Error loading opening hours:', err);
        container.innerHTML = '<div style="color: #EF4444;">Impossibile caricare gli orari</div>';
      });
  })();
</script>`;

      setEmbedCode(code);
    } catch (error) {
      console.error('Failed to generate embed code:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviewData = async () => {
    if (!restaurantId) return;
    
    setPreviewLoading(true);
    try {
      const apiBaseURL = axios.defaults.baseURL || 'http://localhost:5000/api';
      // Use the public endpoint that includes special hours for current date
      const response = await axios.get(`${apiBaseURL}/restaurants/${restaurantId}/public-opening-hours`);
      
      if (response.data.success) {
        setPreviewData(response.data.data);
        console.log('Preview data loaded (includes special hours):', response.data.data);
      } else {
        setPreviewData(null);
      }
    } catch (error) {
      console.error('Error fetching preview data:', error);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Simplified special hours fetch - only needed for the date selector
  const fetchSpecialHours = async () => {
    if (!restaurantId) return;
    
    try {
      const apiBaseURL = axios.defaults.baseURL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      console.log('=== FETCHING SPECIAL HOURS FOR DATE SELECTOR ===');
      
      const url = `${apiBaseURL}/restaurants/${restaurantId}/special-opening-hours`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const specialHoursData = response.data.data || [];
        console.log('Setting special hours for date selector:', specialHoursData);
        setSpecialHours(specialHoursData);
      }
    } catch (error: any) {
      console.error('Error fetching special hours for selector:', error);
      setSpecialHours([]);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success(t('opening_hours_embed.messages.copy_success'));
    setTimeout(() => setCopied(false), 2000);
  };

  // Get special hours for a specific date
  const getSpecialHoursForDate = (date: string) => {
    console.log('getSpecialHoursForDate called with:', date);
    console.log('Available special hours:', specialHours);
    
    if (!date || specialHours.length === 0) {
      console.log('No date or no special hours available');
      return null;
    }
    
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    console.log('Target date (normalized):', targetDate);
    
    const found = specialHours.find(specialHour => {
      const specialDate = new Date(specialHour.date);
      specialDate.setHours(0, 0, 0, 0);
      const isMatch = specialDate.getTime() === targetDate.getTime();
      console.log(`Comparing ${specialHour.date} (${specialDate.getTime()}) with ${date} (${targetDate.getTime()}): ${isMatch ? 'MATCH' : 'NO MATCH'}`);
      return isMatch;
    });
    
    console.log('Found special hour result:', found);
    return found;
  };

  // Simplified merge logic - preview data already includes today's special hours
  const getMergedHoursForPreview = () => {
    // The previewData from /public-opening-hours already includes special hours for today
    // For selected dates, we still need to manually override
    
    if (!previewData) return null;
    
    // If no date selected, return the data as-is (already includes today's special hours)
    if (!selectedDate) {
      return previewData;
    }
    
    // For selected dates, manually override the corresponding day
    const specialHour = getSpecialHoursForDate(selectedDate);
    if (!specialHour) return previewData;
    
    const mergedData = { ...previewData };
    const dateObj = new Date(selectedDate);
    const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    
    // Override that day with selected special hours
    mergedData[dayOfWeek] = {
      isOpen: specialHour.isOpen,
      singleSlot: specialHour.singleSlot,
      lunchSlot: specialHour.lunchSlot,
      dinnerSlot: specialHour.dinnerSlot
    };
    
    return mergedData;
  };

  const fontFamilies = [
    'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
    'Playfair Display', 'Merriweather', 'Georgia', 'Times New Roman',
    'Courier New', 'Monaco'
  ];

  const fontSizes = ['12px', '14px', '16px', '18px', '20px'];
  const fontWeights = ['300', '400', '500', '600', '700'];

  // Get available dates for special hours
  const getAvailableDates = () => {
    console.log('getAvailableDates called');
    console.log('Raw special hours:', specialHours);
    
    // Filter for future dates (no isActive check since it might not exist)
    const filtered = specialHours.filter(sh => {
      const isFuture = new Date(sh.date) >= new Date();
      console.log(`Checking ${sh.date}: isFuture=${isFuture}`);
      return isFuture;
    });
    
    console.log('Filtered special hours:', filtered);
    
    const sorted = filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    console.log('Sorted special hours:', sorted);
    
    const mapped = sorted.map(sh => ({
      date: sh.date,
      display: new Date(sh.date).toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      })
    }));
    
    console.log('Mapped dates:', mapped);
    return mapped;
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Special Hours Date Selector - Compact Top Bar */}
      {specialHours.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Codice di Incorporamento Orari Apertura</span>
            </div>
            <div className="flex flex-col xs:flex-row gap-2 flex-1">
              <select
                value={selectedDate}
                onChange={(e) => {
                  console.log('Date selected:', e.target.value);
                  setSelectedDate(e.target.value);
                }}
                className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm bg-white flex-1"
              >
                <option value="">{t('opening_hours_embed.special_hours_selector.regular_hours')}</option>
                {getAvailableDates().map(({ date, display }) => (
                  <option key={date} value={date}>
                    {display}
                  </option>
                ))}
              </select>
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate('')}
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm whitespace-nowrap"
                >
                  {t('opening_hours_embed.special_hours_selector.clear')}
                </button>
              )}
            </div>
          </div>
          {selectedDate && (
            <div className="mt-2 text-sm text-blue-700">
              {t('opening_hours_embed.special_hours_selector.showing_special')} {new Date(selectedDate).toLocaleDateString('it-IT', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
            <Code className="w-5 h-5 md:w-6 md:h-6" />
            {t('opening_hours_embed.title')}
          </h3>
          <p className="text-gray-600 text-sm md:text-base mt-1">{t('opening_hours_embed.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-3 py-2 md:px-4 md:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm md:text-base flex-1 sm:flex-none"
          >
            <Eye className="w-4 h-4" />
            <span className="hidden xs:inline">{showPreview ? t('opening_hours_embed.buttons.hide_preview') : t('opening_hours_embed.buttons.show_preview')}</span>
            <span className="inline xs:hidden">{t('opening_hours_embed.buttons.preview')}</span>
          </button>
          <button
            onClick={copyToClipboard}
            className="px-3 py-2 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm md:text-base flex-1 sm:flex-none"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t('opening_hours_embed.embed_code.copied') : t('opening_hours_embed.embed_code.copy_code')}
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Style Configuration - Collapsible on mobile */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <button
            onClick={() => setIsStyleConfigOpen(!isStyleConfigOpen)}
            className="w-full p-4 md:p-6 flex items-center justify-between lg:hidden"
          >
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <Settings className="w-5 h-5" />
             Configurazione Stile
            </h4>
            {isStyleConfigOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          <div className={`${isMobileView && !isStyleConfigOpen ? 'hidden' : ''}`}>
            <div className="hidden lg:block p-6 pb-0">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                {t('opening_hours_embed.style_config.title')}--
              </h4>
            </div>
            
            <div className="p-4 md:p-6 pt-0 lg:pt-4 space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('opening_hours_embed.style_config.font_family')}
                  </label>
                  <select
                    value={styleConfig.fontFamily}
                    onChange={(e) => setStyleConfig({ ...styleConfig, fontFamily: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                  >
                    {fontFamilies.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('opening_hours_embed.style_config.font_size')}
                    </label>
                    <select
                      value={styleConfig.fontSize}
                      onChange={(e) => setStyleConfig({ ...styleConfig, fontSize: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                    >
                      {fontSizes.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('opening_hours_embed.style_config.font_weight')}
                    </label>
                    <select
                      value={styleConfig.fontWeight}
                      onChange={(e) => setStyleConfig({ ...styleConfig, fontWeight: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                    >
                      {fontWeights.map(weight => (
                        <option key={weight} value={weight}>{weight}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('opening_hours_embed.style_config.text_color')}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={styleConfig.textColor}
                        onChange={(e) => setStyleConfig({ ...styleConfig, textColor: e.target.value })}
                        className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={styleConfig.textColor}
                        onChange={(e) => setStyleConfig({ ...styleConfig, textColor: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                        placeholder="#111827"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('opening_hours_embed.style_config.background_color')}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={styleConfig.backgroundColor}
                        onChange={(e) => setStyleConfig({ ...styleConfig, backgroundColor: e.target.value })}
                        className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={styleConfig.backgroundColor}
                        onChange={(e) => setStyleConfig({ ...styleConfig, backgroundColor: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                        placeholder="#FFFFFF"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('opening_hours_embed.style_config.border_color')}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={styleConfig.borderColor}
                      onChange={(e) => setStyleConfig({ ...styleConfig, borderColor: e.target.value })}
                      className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={styleConfig.borderColor}
                      onChange={(e) => setStyleConfig({ ...styleConfig, borderColor: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                      placeholder="#E5E7EB"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('opening_hours_embed.style_config.border_radius')}
                    </label>
                    <input
                      type="text"
                      value={styleConfig.borderRadius}
                      onChange={(e) => setStyleConfig({ ...styleConfig, borderRadius: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                      placeholder="8px"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('opening_hours_embed.style_config.padding')}
                    </label>
                    <input
                      type="text"
                      value={styleConfig.padding}
                      onChange={(e) => setStyleConfig({ ...styleConfig, padding: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                      placeholder="16px"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('opening_hours_embed.style_config.width')}
                  </label>
                  <input
                    type="text"
                    value={styleConfig.width}
                    onChange={(e) => setStyleConfig({ ...styleConfig, width: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                    placeholder="100%"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview and Code */}
        <div className="space-y-4">
          {showPreview && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  {t('opening_hours_embed.preview.title')}
                </h4>
                {/* Special Hours Indicator */}
                {selectedDate && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-600 font-medium">
                      {t('opening_hours_embed.preview.special_indicator')} {new Date(selectedDate).toLocaleDateString('it-IT', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>
              <div
                style={{
                  fontFamily: styleConfig.fontFamily,
                  fontSize: styleConfig.fontSize,
                  fontWeight: styleConfig.fontWeight,
                  color: styleConfig.textColor,
                  backgroundColor: styleConfig.backgroundColor,
                  border: `1px solid ${styleConfig.borderColor}`,
                  borderRadius: styleConfig.borderRadius,
                  padding: styleConfig.padding,
                  width: styleConfig.width
                }}
                className="min-h-[80px] md:min-h-[100px] overflow-x-auto"
              >
                {previewLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">{t('opening_hours_embed.preview.loading')}</span>
                  </div>
                ) : getMergedHoursForPreview() ? (
                  <div>
                    <div className="font-semibold mb-3" style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '1.1em' }}>
                      {t('opening_hours_embed.messages.opening_hours')}
                      {selectedDate && (
                        <span className="text-sm font-normal text-blue-600 ml-2">
                          ({t('opening_hours_embed.special_hours_selector.showing_special')} {new Date(selectedDate).toLocaleDateString('it-IT', { month: 'short', day: 'numeric' })})
                        </span>
                      )}
                    </div>
                    
                    {/* Group consecutive days with same hours */}
                    {(() => {
                      const mergedHours = getMergedHoursForPreview();
                      // Normalize slot from API (object or JSON string)
                      const parseSlot = (slot: unknown): { open: string; close: string } | null => {
                        if (slot == null) return null;
                        if (typeof slot === 'string') {
                          try {
                            const o = JSON.parse(slot) as { open?: string; close?: string };
                            return o && (o.open != null || o.close != null) ? { open: o.open ?? '', close: o.close ?? '' } : null;
                          } catch { return null; }
                        }
                        if (typeof slot === 'object' && slot !== null && 'open' in (slot as object)) {
                          const s = slot as { open?: string; close?: string };
                          return { open: s.open ?? '', close: s.close ?? '' };
                        }
                        return null;
                      };
                      
                      const groups = [];
                      let currentGroup = null;
                      const orderedDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                      
                      for (let i = 0; i < orderedDays.length; i++) {
                        const dayKey = orderedDays[i];
                        const dayHours = mergedHours ? mergedHours[dayKey] : null;
                        const lunch = dayHours ? parseSlot(dayHours.lunchSlot) : null;
                        const dinner = dayHours ? parseSlot(dayHours.dinnerSlot) : null;
                        const single = dayHours ? parseSlot(dayHours.singleSlot) : null;
                        
                        let signature = '';
                        if (dayHours && dayHours.isOpen) {
                          if (lunch && dinner && lunch.open && lunch.close && dinner.open && dinner.close) {
                            signature = `split:${lunch.open}-${lunch.close}:${dinner.open}-${dinner.close}`;
                          } else if (single && single.open && single.close) {
                            signature = `single:${single.open}-${single.close}`;
                          } else {
                            signature = 'closed';
                          }
                        } else {
                          signature = 'closed';
                        }
                        
                        console.log(`${dayKey} signature:`, signature);
                        
                        if (!currentGroup || currentGroup.signature !== signature) {
                          if (currentGroup) {
                            groups.push(currentGroup);
                          }
                          currentGroup = {
                            days: [dayKey],
                            signature: signature,
                            hours: dayHours
                          };
                        } else {
                          currentGroup.days.push(dayKey);
                        }
                      }
                      
                      // Add the last group
                      if (currentGroup) {
                        groups.push(currentGroup);
                      }
                      
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {groups.map((group, index) => {
                            const dayLabels = group.days.map(day => {
                              // Italian 3-letter abbreviations: Lun, Mar, Mer, etc.
                              const abbreviations: Record<string, string> = {
                                'monday': 'Lun',
                                'tuesday': 'Mar',
                                'wednesday': 'Mer',
                                'thursday': 'Gio',
                                'friday': 'Ven',
                                'saturday': 'Sab',
                                'sunday': 'Dom'
                              };
                              return abbreviations[day] || day.substring(0, 3);
                            });
                            
                            let dayDisplay = '';
                            if (group.days.length === 1) {
                              dayDisplay = dayLabels[0];
                            } else if (group.days.length === 2) {
                              dayDisplay = dayLabels[0] + ' & ' + dayLabels[1];
                            } else {
                              dayDisplay = dayLabels[0] + ' - ' + dayLabels[dayLabels.length - 1];
                            }
                            
                            return (
                              <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #E5E7EB' }}>
                                <span style={{ fontWeight: '500', minWidth: '120px' }}>{dayDisplay}:</span>
                                {group.hours && group.hours.isOpen ? (
                                  (() => {
                                    const formatTime24 = (timeStr: string) => {
                                      if (!timeStr) return '';
                                      const p = String(timeStr).trim().split(':');
                                      let h = parseInt(p[0], 10);
                                      let m = p[1] != null ? parseInt(p[1], 10) : 0;
                                      if (isNaN(h)) h = 0;
                                      if (isNaN(m)) m = 0;
                                      return (h < 10 ? '0' + h : '' + h) + ':' + (m < 10 ? '0' + m : '' + m);
                                    };
                                    if (group.signature.startsWith('single:')) {
                                      const timePart = group.signature.substring(7);
                                      const [open, close] = timePart.split('-');
                                      return <span>{formatTime24(open)} a {formatTime24(close)}</span>;
                                    }
                                    if (group.signature.startsWith('split:')) {
                                      const timePart = group.signature.substring(6);
                                      const parts = timePart.split(':');
                                      const lunchOpen = parts[0] + ':' + (parts[1]?.split('-')[0] ?? '00');
                                      const lunchClose = (parts[1]?.split('-')[1] ?? '00') + ':' + (parts[2] ?? '00');
                                      const dinnerOpen = (parts[3] ?? '00') + ':' + (parts[4]?.split('-')[0] ?? '00');
                                      const dinnerClose = (parts[4]?.split('-')[1] ?? '00') + ':' + (parts[5] ?? '00');
                                      return (
                                        <span style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                                          <span>Pranzo: {formatTime24(lunchOpen)} - {formatTime24(lunchClose)}</span>
                                          <span style={{ marginTop: '4px' }}>Cena: {formatTime24(dinnerOpen)} - {formatTime24(dinnerClose)}</span>
                                        </span>
                                      );
                                    }
                                    const h = group.hours;
                                    const fallbackLunch = h ? parseSlot(h.lunchSlot) : null;
                                    const fallbackDinner = h ? parseSlot(h.dinnerSlot) : null;
                                    const fallbackSingle = h ? parseSlot(h.singleSlot) : null;
                                    if (fallbackLunch && fallbackDinner && fallbackLunch.open && fallbackLunch.close && fallbackDinner.open && fallbackDinner.close) {
                                      return (
                                        <span style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                                          <span>Pranzo: {formatTime24(fallbackLunch.open)} - {formatTime24(fallbackLunch.close)}</span>
                                          <span style={{ marginTop: '4px' }}>Cena: {formatTime24(fallbackDinner.open)} - {formatTime24(fallbackDinner.close)}</span>
                                        </span>
                                      );
                                    }
                                    if (fallbackSingle && fallbackSingle.open && fallbackSingle.close) {
                                      return <span>{formatTime24(fallbackSingle.open)} a {formatTime24(fallbackSingle.close)}</span>;
                                    }
                                    return <span style={{ color: '#9CA3AF' }}>Orari non disponibili</span>;
                                  })()
                                ) : (
                                  <span style={{ color: '#EF4444', fontWeight: '500' }}>Chiuso</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div>
                    <div className="font-semibold mb-2">{t('opening_hours_embed.preview.title')}</div>
                    <div>{t('opening_hours_embed.preview.no_data')}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-gray-900 rounded-lg p-3 md:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
              <h4 className="font-semibold text-white text-sm md:text-base">{t('opening_hours_embed.embed_code.title')}</h4>
              <button
                onClick={copyToClipboard}
                className="px-3 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 text-xs md:text-sm flex items-center gap-1 self-end sm:self-auto"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? t('opening_hours_embed.embed_code.copied') : t('opening_hours_embed.embed_code.copy_code')}
              </button>
            </div>
            <pre className="text-xs md:text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap break-all">
              <code>{embedCode}</code>
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpeningHoursEmbedEditor;